import React, { useCallback, useEffect, useRef, useState } from "react";
import Modal from "../../Atomos/Modals/Modal";
import NewTooltip from "../../Atomos/Tooltip/NewTooltip";
import NewButton from "../../Atomos/Buttons/newButton";
import ProfilePhoto from "../../Atomos/ProfilePhoto";
import { resolveProfilePictureDisplayUrl } from "../../../utils/profilePictureUrl";
import { PROFILE_PHOTO_UPDATED_EVENT } from "../../../utils/notifyProfilePhotoUpdated";

const MAX_IMAGE_SIZE = 25 * 1024 * 1024;
const ACCEPTED_EXTENSIONS = [
  ".jpg",
  ".jpeg",
  ".png",
  ".gif",
  ".webp",
  ".heif",
  ".bmp",
  ".tiff",
  ".dng",
  ".raw",
];

type ImagePreviewUploadProps = {
  maxsize?: number;
  photo?: string;
  setImage: (photo: string | File | null) => void;
  title?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  required?: boolean;
  tooltip?: string;
  error?: boolean;
  errorMsg?: string;
  hideBorder?: boolean;
  modalClassName?: string;
  extension?: string;
  personId?: string;
  photoUpdatedAt?: number;
  /** Shows a Remove photo action while there is a photo; it calls `setImage(null)`. */
  removable?: boolean;
};

const isAcceptedImage = (file: File) => {
  const ext = `.${file.name.split(".").pop()?.toLowerCase() ?? ""}`;
  return ACCEPTED_EXTENSIONS.includes(ext) || file.type.startsWith("image/");
};

const ImagePreviewUpload: React.FC<ImagePreviewUploadProps> = (props) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const [confirmedPreviewUrl, setConfirmedPreviewUrl] = useState<string | null>(
    null,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [photoRenderKey, setPhotoRenderKey] = useState(0);
  /**
   * The stored photo URL is built from `personId` alone, so an emptied `photo` would still
   * show the old picture until the removal is saved.
   */
  const [removed, setRemoved] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewObjectUrlRef = useRef<string | null>(null);

  const revokePreviewUrl = useCallback(() => {
    if (previewObjectUrlRef.current) {
      URL.revokeObjectURL(previewObjectUrlRef.current);
      previewObjectUrlRef.current = null;
    }
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setPendingFile(null);
    setPreviewSrc(null);
    setErrorMessage(null);
    revokePreviewUrl();
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    document.body.style.overflow = "";
  }, [revokePreviewUrl]);

  useEffect(() => {
    return () => {
      revokePreviewUrl();
      if (confirmedPreviewUrl) {
        URL.revokeObjectURL(confirmedPreviewUrl);
      }
    };
  }, [revokePreviewUrl, confirmedPreviewUrl]);

  useEffect(() => {
    if (!props.personId) {
      return;
    }

    const onProfilePhotoUpdated = (event: Event) => {
      const { personId } = (
        event as CustomEvent<{ personId: string; cacheBust: number }>
      ).detail;
      if (personId && personId === props.personId) {
        setPhotoRenderKey((k) => k + 1);
      }
    };

    document.addEventListener(PROFILE_PHOTO_UPDATED_EVENT, onProfilePhotoUpdated);
    return () => {
      document.removeEventListener(
        PROFILE_PHOTO_UPDATED_EVENT,
        onProfilePhotoUpdated,
      );
    };
  }, [props.personId]);

  const openFilePicker = () => {
    if (props.disabled) {
      return;
    }
    fileInputRef.current?.click();
  };

  const processFile = (file: File) => {
    setErrorMessage(null);
    if (!isAcceptedImage(file)) {
      setErrorMessage("File type not supported.");
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      setErrorMessage("Image must be 25 MB or smaller.");
      return;
    }

    revokePreviewUrl();
    const objectUrl = URL.createObjectURL(file);
    previewObjectUrlRef.current = objectUrl;
    setPendingFile(file);
    setPreviewSrc(objectUrl);
    setIsModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (props.disabled) {
      return;
    }
    const file = e.dataTransfer.files[0];
    if (file) {
      processFile(file);
    }
  };

  const handleConfirm = () => {
    if (!pendingFile) {
      return;
    }
    if (confirmedPreviewUrl) {
      URL.revokeObjectURL(confirmedPreviewUrl);
    }
    const nextPreviewUrl = URL.createObjectURL(pendingFile);
    setConfirmedPreviewUrl(nextPreviewUrl);
    setRemoved(false);
    props.setImage(pendingFile);
    closeModal();
  };

  useEffect(() => {
    if (props.photo?.trim()) {
      setRemoved(false);
    }
  }, [props.photo]);

  const handleRemove = () => {
    if (props.disabled) {
      return;
    }
    if (confirmedPreviewUrl) {
      URL.revokeObjectURL(confirmedPreviewUrl);
      setConfirmedPreviewUrl(null);
    }
    setRemoved(true);
    props.setImage(null);
  };

  const hasPhoto =
    Boolean(confirmedPreviewUrl) || (!removed && Boolean(props.photo?.trim()));

  const displaySrc =
    confirmedPreviewUrl ??
    (removed
      ? ""
      : props.personId
      ? resolveProfilePictureDisplayUrl(props.personId, props.photo, {
          width: 360,
          height: 360,
          photoUpdatedAt: props.photoUpdatedAt,
        })
      : props.photo);

  return (
    <>
      <Modal
        title={props.title ?? "Upload Profile Image"}
        isModalOpen={isModalOpen}
        toggleModal={closeModal}
        className={props.modalClassName}
      >
        <div className="flex flex-col gap-5">
          {previewSrc ? (
            <div className="flex justify-center rounded-lg bg-[#F4F4F4] p-4">
              <img
                src={previewSrc}
                alt="Photo preview"
                className="max-h-[min(400px,60vh)] max-w-full object-contain"
              />
            </div>
          ) : (
            <p className="text-center text-sm text-gray-font">
              Choose an image to preview before uploading.
            </p>
          )}

          {errorMessage && (
            <p className="text-sm text-feedback-error">{errorMessage}</p>
          )}

          <div className="flex flex-wrap justify-end gap-3">
            <NewButton
              text="Choose different"
              style="outlined"
              color="neutral"
              onClick={openFilePicker}
            />
            <NewButton
              text="Use this photo"
              style="filled"
              color="primary"
              onClick={handleConfirm}
              disabled={!pendingFile}
            />
          </div>
        </div>
      </Modal>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileInput}
        style={{ display: "none" }}
      />

      <div
        className={`flex h-full flex-col gap-2 lg:gap-6 ${props.hideBorder ? "" : "border p-2"} ${props.error ? "errorInput" : ""}`}
      >
        <div
          className={`flex justify-center not-italic font-bold ${props.disabled ? "text-[#C2C2C2]" : "text-gray-font"}`}
        >
          <p className="font-semibold">
            {props.title}
            {props.required && <span className="text-primary"> * </span>}
            {props.tooltip && <NewTooltip text={props.tooltip} />}
          </p>
        </div>

        <div className="flex flex-col items-center gap-2 [&>*:last-child]:gap-0">
          <div className="flex h-full w-full flex-col items-center justify-center gap-2">
            <div
              className={`relative flex h-[135px] w-[135px] items-center justify-center overflow-hidden rounded-full border border-dashed border-[#BEBDBD] bg-[#F4F4F4] lg:h-[204px] lg:w-[204px] ${props.disabled ? "" : "cursor-pointer"}`}
              onClick={openFilePicker}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
              role="button"
              tabIndex={props.disabled ? -1 : 0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openFilePicker();
                }
              }}
            >
              <div className="relative flex h-full w-full items-center justify-center">
                <ProfilePhoto
                  src={displaySrc}
                  alt="Profile"
                  imgKey={`${props.personId ?? "photo"}-${photoRenderKey}`}
                  imgClassName="h-full w-full rounded-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center text-sm text-gray-600">
          <span className="not-italic text-gray-600">Click or Drag</span>
          {props.maxsize && (
            <span className="not-italic">{props.maxsize} MB max.</span>
          )}
          {props.extension && (
            <span className="text-center not-italic">
              Accepted File Types are: {props.extension}.
            </span>
          )}
          {props.removable && hasPhoto && (
            <button
              type="button"
              className="mt-2 text-sm font-medium text-primary underline underline-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={props.disabled}
              onClick={handleRemove}
            >
              Remove photo
            </button>
          )}
        </div>

        {props.errorMsg && (
          <div className="flex w-full flex-wrap items-center justify-center text-base font-medium text-primary">
            {props.errorMsg}
          </div>
        )}
      </div>
    </>
  );
};

export default ImagePreviewUpload;
